import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  FormControl,
  InputLabel,
  TextField,
  Pagination,
  SelectChangeEvent,
} from "@mui/material";
import { LuSearch, LuEye } from "react-icons/lu";
import {
  OFF_WHITE,
  muiFieldSx,
  filterFieldSx,
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
} from "../../constants/layout";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";
const PROFILE_FONT = '"Inter", sans-serif';
const BORDER = "#E8E8E8";
const fieldSx = muiFieldSx;

const cardSx = {
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: CARD_SHADOW,
  border: "1px solid rgba(0,0,0,0.04)",
};

const REQUEST_STATUS: Record<number, { label: string }> = {
  0: { label: "Pending" },
  1: { label: "Accepted" },
  2: { label: "Rejected" },
};

const getStatusConfig = (status: number) => REQUEST_STATUS[status] ?? REQUEST_STATUS[0];

interface Notification {
  id: number;
  notifyTitle: string;
  notifyMessage: string;
  createdAt: string;
  userName?: string;
  requestStatus?: number;
}

interface RequestsTableProps {
  requests: Notification[];
  totalPages: number;
  rowsPerPage: number;
  page: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  loading: boolean;
  error: string | null;
  onChangeStatus: (requestId: number, status: number) => void;
  onOpenDetails: (id: number) => void;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const RequestsTable: React.FC<RequestsTableProps> = ({
  requests,
  totalPages,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
  loading,
  error,
  onChangeStatus,
  onOpenDetails,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<number>(-1);
  const [loadingRowId, setLoadingRowId] = useState<number | null>(null);

  const sortedRequests = useMemo(
    () =>
      [...requests].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [requests]
  );

  const filteredRequests = useMemo(() => {
    return sortedRequests.filter((request) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        request.notifyTitle?.toLowerCase().includes(q) ||
        request.notifyMessage?.toLowerCase().includes(q) ||
        request.userName?.toLowerCase().includes(q);

      const status = request.requestStatus ?? 0;
      const matchesStatus = statusFilter === -1 || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [sortedRequests, searchQuery, statusFilter]);

  const handleChangeStatus = async (requestId: number, status: number) => {
    setLoadingRowId(requestId);
    await onChangeStatus(requestId, status);
    setTimeout(() => setLoadingRowId(null), 2000);
  };

  const currentPage = page + 1;
  const totalRecords = totalPages * rowsPerPage;
  const from = requests.length === 0 ? 0 : page * rowsPerPage + 1;
  const to = requests.length === 0 ? 0 : page * rowsPerPage + requests.length;

  const headCellSx = {
    fontFamily: PROFILE_FONT,
    fontSize: 12,
    fontWeight: 600,
    color: DARK_NAVY_LIGHT,
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    borderBottom: `1px solid ${BORDER}`,
    py: 1.75,
    bgcolor: "#FAFAFA",
  };

  const bodyCellSx = {
    fontFamily: PROFILE_FONT,
    fontSize: 14,
    color: DARK_NAVY,
    borderBottom: `1px solid #F5F5F5`,
    py: 1.75,
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: { xs: 24, md: 28 },
              fontWeight: 700,
              color: DARK_NAVY,
              mb: 0.5,
            }}
          >
            All Requests
          </Typography>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
            Manage all investor requests raised across AIF and PMS.
          </Typography>
        </Box>

        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { xs: "stretch", md: "center" },
            }}
          >
            <TextField
              placeholder="Search by title, message or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              variant="outlined"
              sx={{ ...filterFieldSx, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", pl: 0.5, pr: 1, color: DARK_NAVY_LIGHT }}>
                    <LuSearch size={18} />
                  </Box>
                ),
              }}
            />

            <FormControl size="small" sx={{ ...filterFieldSx, minWidth: { xs: "100%", md: 200 } }}>
              <InputLabel id="request-status-filter">Status</InputLabel>
              <Select
                labelId="request-status-filter"
                value={statusFilter}
                onChange={(e: SelectChangeEvent<number>) => setStatusFilter(Number(e.target.value))}
                label="Status"
              >
                <MenuItem value={-1}>All Status</MenuItem>
                <MenuItem value={0}>Pending</MenuItem>
                <MenuItem value={1}>Accepted</MenuItem>
                <MenuItem value={2}>Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ ...cardSx, overflow: "hidden" }}>
          <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Title</TableCell>
                      <TableCell sx={headCellSx}>Message</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Created At</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 72 }} align="center">
                        Details
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {error ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ ...bodyCellSx, textAlign: "center", py: 6 }}>
                          <Typography sx={{ fontFamily: PROFILE_FONT, color: DARK_NAVY_LIGHT }}>{error}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ ...bodyCellSx, textAlign: "center", py: 6 }}>
                          <Typography sx={{ fontFamily: PROFILE_FONT, color: DARK_NAVY_LIGHT }}>
                            No requests found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRequests.map((request) => {
                        const status = request.requestStatus ?? 0;
                        const isPending = status !== 1 && status !== 2;

                        return (
                          <TableRow
                            key={request.id}
                            hover
                            sx={{ "&:hover": { bgcolor: "#FAFAFA" }, "&:last-child td": { borderBottom: "none" } }}
                          >
                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{request.notifyTitle}</TableCell>
                            <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT, maxWidth: 320 }}>
                              {request.notifyMessage}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              {loadingRowId === request.id ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <CircularProgress size={16} sx={{ color: ICON_BLUE }} />
                                  <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT }}>
                                    Updating...
                                  </Typography>
                                </Box>
                              ) : isPending ? (
                                <FormControl size="small" sx={{ minWidth: 150, ...fieldSx }}>
                                  <Select
                                    value=""
                                    displayEmpty
                                    onChange={(e) =>
                                      handleChangeStatus(request.id, Number(e.target.value))
                                    }
                                    renderValue={() => getStatusConfig(0).label}
                                    sx={{
                                      "& .MuiSelect-select": {
                                        py: 0.5,
                                        display: "flex",
                                        alignItems: "center",
                                        color: ICON_BLUE,
                                        fontWeight: 600,
                                        fontSize: 13,
                                        fontFamily: PROFILE_FONT,
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled sx={{ display: "none" }}>
                                      Pending
                                    </MenuItem>
                                    <MenuItem value={1}>Accept</MenuItem>
                                    <MenuItem value={2}>Reject</MenuItem>
                                  </Select>
                                </FormControl>
                              ) : (
                                <Typography
                                  sx={{
                                    fontFamily: PROFILE_FONT,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: ICON_BLUE,
                                  }}
                                >
                                  {getStatusConfig(status).label}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }}>
                              {formatDate(request.createdAt)}
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="center">
                              <IconButton
                                size="small"
                                aria-label="View details"
                                onClick={() => onOpenDetails(request.id)}
                                sx={{
                                  color: DARK_NAVY_LIGHT,
                                  borderRadius: "8px",
                                  "&:hover": { bgcolor: OFF_WHITE, color: ICON_BLUE },
                                }}
                              >
                                <LuEye size={18} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  px: { xs: 2, md: 2.5 },
                  py: 2,
                  borderTop: `1px solid ${BORDER}`,
                }}
              >
                <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT }}>
                  Showing {from} to {to} of {totalRecords} requests
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ ...fieldSx, minWidth: 130 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={(e: SelectChangeEvent<number>) =>
                        onRowsPerPageChange(Number(e.target.value))
                      }
                    >
                      <MenuItem value={10}>10 per page</MenuItem>
                      <MenuItem value={25}>25 per page</MenuItem>
                      <MenuItem value={50}>50 per page</MenuItem>
                    </Select>
                  </FormControl>

                  <Pagination
                    count={totalPages || 1}
                    page={currentPage}
                    onChange={(_, p) => onPageChange(null, p - 1)}
                    shape="rounded"
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontFamily: PROFILE_FONT,
                        fontSize: 13,
                        color: DARK_NAVY_LIGHT,
                        borderRadius: "8px",
                      },
                      "& .Mui-selected": {
                        bgcolor: `${ICON_BLUE} !important`,
                        color: "#ffffff !important",
                      },
                    }}
                  />
                </Box>
              </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RequestsTable;
